import api from './api';
import studyService from './studyService';
import surveyService from './surveyService';

const studyBuilderService = {
  createStudyDraft: async (studyName) => {
    try {
      // First create the actual study
      const studyPayload = {
        title: studyName,
        description: '',
        status: 'planned',
        is_byod_allowed: false,
        code: studyName.toLowerCase().replace(/[^a-z0-9]/g, '-'),
      };
      
      const studyResponse = await studyService.createStudy(studyPayload);
      console.log('Created study:', studyResponse);
      
      // Extract study ID from the nested response
      const studyId = studyResponse?.study?.id;
      console.log('Extracted study ID:', studyId);
      
      if (!studyId) {
        throw new Error('Failed to get study ID from created study');
      }
      
      // Store the study ID for later use
      localStorage.setItem('currentStudyId', studyId.toString());
      console.log('Stored study ID in localStorage:', studyId);
      
      // Then create the study draft
      const draftPayload = {
        name: studyName,
        status: 'Incomplete',
        data: {},
        study: studyId, // Link the draft to the created study
      };
      
      console.log('Creating study draft with payload:', draftPayload);
      const response = await api.post('/api/v1/study-builder/study-drafts/', draftPayload);
      console.log('Study draft creation response:', response.data);
      
      // Store the study draft ID
      const studyDraftId = response.data?.id;
      if (studyDraftId) {
        localStorage.setItem('currentStudyDraftId', studyDraftId.toString());
        console.log('Stored study draft ID in localStorage:', studyDraftId);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error in createStudyDraft:', error);
      // Rethrow the original error so callers can inspect response status/data
      throw error;
    }
  },

  getStudyDraft: async (draftId) => {
    try {
      const response = await api.get(`/api/v1/study-builder/study-drafts/${draftId}/`);
      return response.data;
    } catch (error) {
      console.error('Error in getStudyDraft:', error);
      throw error.response?.data || error.message;
    }
  },
  
  getStudies: async () => {
    // Use the studyService's getStudies function instead
    return studyService.getStudies();
  },

  createSurvey: async (surveyData, studyName) => {
    console.log('StudyBuilderService received survey data:', surveyData);
    return surveyService.createSurvey(surveyData, studyName);
  },

  updateStudyDesign: async (studyName, designData) => {
    try {
      const studyDraftId = localStorage.getItem('currentStudyDraftId');
      if (!studyDraftId) {
        throw new Error('No study draft ID found');
      }

      // Utility function to convert local time to UTC hours/minutes
      const convertToUTC = (time) => {
        // Debug the incoming time object
        console.log('Converting to UTC:', time);
        
        // Skip conversion if already in UTC format or if time is not provided
        if (!time || time.period === 'UTC') {
          console.log('Already in UTC format, returning as-is');
          return time;
        }
        
        try {
          // Parse hours and minutes
          let hours = parseInt(time.hours, 10);
          const minutes = parseInt(time.minutes, 10);
          
          // Validate input
          if (isNaN(hours) || isNaN(minutes)) {
            console.error('Invalid time format:', time);
            return time; // Return original if invalid
          }
          
          // Convert from 12-hour to 24-hour format
          if (time.period === 'PM' && hours !== 12) {
            hours += 12;
          } else if (time.period === 'AM' && hours === 12) {
            hours = 0;
          }
          
          // Use a fixed reference date to avoid DST issues
          // Create a date representing the input time in the local timezone
          const localDate = new Date();
          localDate.setHours(hours, minutes, 0, 0);
          
          // Get UTC hours and minutes
          const utcHours = localDate.getUTCHours();
          const utcMinutes = localDate.getUTCMinutes();
          
          // Format and return the UTC time
          const result = {
            hours: utcHours.toString().padStart(2, '0'),
            minutes: utcMinutes.toString().padStart(2, '0'),
            period: 'UTC'
          };
          
          console.log(`Converted local time ${hours}:${minutes} to UTC:`, result);
          return result;
        } catch (error) {
          console.error('Error converting to UTC:', error);
          return time; // Return original on error
        }
      };

      // Clean and validate the schedules data
      if (designData.schedules) {
        designData.schedules = designData.schedules.map(schedule => {
          const cleanedSchedule = {
            ...schedule,
            // Ensure boolean value for hasAssessmentWindow
            hasAssessmentWindow: schedule.hasAssessmentWindow === true,
            // Convert reminder times to UTC
            emailReminderTimers: (schedule.emailReminderTimers || []).map(convertToUTC),
            pushReminderTimers: (schedule.pushReminderTimers || []).map(convertToUTC),
            stages: schedule.stages.map(stage => ({
              ...stage,
              startDay: parseInt(stage.startDay),
              endDay: parseInt(stage.endDay),
              frequency: {
                ...stage.frequency,
                value: parseInt(stage.frequency.value)
              }
            }))
          };

          // Include assessment windows if present and enabled. Times are
          // converted to UTC per window before persisting.
          if (cleanedSchedule.hasAssessmentWindow === true && Array.isArray(schedule.assessmentWindows)) {
            cleanedSchedule.assessmentWindows = schedule.assessmentWindows.map((w) => ({
              name: w.name || '',
              openWindowDay: parseInt(w.openWindowDay),
              closeWindowDay: parseInt(w.closeWindowDay),
              startTime: convertToUTC(w.startTime),
              endTime: convertToUTC(w.endTime)
            }));
          } else {
            cleanedSchedule.assessmentWindows = [];
          }

          return cleanedSchedule;
        });
      }

      // Get the current study draft data first
      const currentDraft = await api.get(`/api/v1/study-builder/study-drafts/${studyDraftId}/`);
      const currentData = currentDraft.data.data || {};

      // Merge the new design data with existing data
      const response = await api.patch(
        `/api/v1/study-builder/study-drafts/${studyDraftId}/`,
        {
          study: currentDraft.data.study,  // Include the study ID from the current draft
          data: {
            ...currentData,
            arms: designData.arms.filter(arm => arm.trim() !== ''),
            schedules: designData.schedules.map(schedule => ({
              name: schedule.name,
              type: schedule.type,
              studyStartDay: parseInt(schedule.studyStartDay, 10),
              dailyStartDay: schedule.dailyStartDay === '' || schedule.dailyStartDay == null
                ? null
                : parseInt(schedule.dailyStartDay, 10),
              dailyDuration: schedule.dailyDuration === '' || schedule.dailyDuration == null
                ? null
                : parseInt(schedule.dailyDuration, 10),
              arms: schedule.arms,
              siteType: schedule.siteType || '',
              // FECOA-1522: persist who answers this schedule. No backend
              // constraint ties this to the surveys' types, so a caregiver can
              // act as a proxy respondent on a PRO assessment if ever needed.
              respondentType: schedule.respondentType || 'participant',

              standardSurveys: schedule.standardSurveys || currentData.schedules.find(s => s.name === schedule.name)?.standardSurveys || [],
              enablePushReminders: schedule.enablePushReminders || false,
              pushReminders: schedule.pushReminders || [],
              pushReminderTimers: schedule.pushReminderTimers || [{ hours: '12', minutes: '00', period: 'AM' }],
              enableEmailReminders: schedule.enableEmailReminders || false,
              emailReminders: schedule.emailReminders || [],
              emailReminderTimers: schedule.emailReminderTimers || [{ hours: '12', minutes: '00', period: 'AM' }],
              enableMissingForms: schedule.enableMissingForms || false,
              missingForms: schedule.missingForms || [],

              // Include assessment timers if type includes "Patient Prompted - Timer"
              ...(schedule.type && schedule.type.includes('Patient Prompted - Timer') && {
                assessmentTimers: schedule.assessmentTimers || [{
                  enabled: true,
                  minutes: 15,
                  addAlarm: false
                }]
              }),

              hasAssessmentWindow: schedule.hasAssessmentWindow === true,
              assessmentWindows: schedule.hasAssessmentWindow ? (schedule.assessmentWindows || []) : [],
              timepoints: Array.isArray(schedule.timepoints)
                ? schedule.timepoints.map(tp => ({
                    id: tp.id,
                    name: tp.name,
                    target_time: tp.target_time,
                    window_minutes: parseInt(tp.window_minutes, 10),
                    surveys: (tp.surveys || []).map(String),
                    reminders: tp.reminders || {},
                  }))
                : [],
              stages: schedule.stages.map(stage => ({
                name: stage.name,
                startDay: parseInt(stage.startDay, 10),
                endDay: parseInt(stage.endDay, 10),
                frequency: {
                  value: parseInt(stage.frequency.value, 10),
                  unit: stage.frequency.unit
                }
              }))
            }))
            
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating study design:', error);
      throw error;
    }
  },

  getStudyDraftData: async () => {
    try {
      const studyDraftId = localStorage.getItem('currentStudyDraftId');
      if (!studyDraftId) {
        return null;
      }

      const response = await api.get(`/api/v1/study-builder/study-drafts/${studyDraftId}/`);
      const data = response.data.data;

      // Ensure proper boolean values for assessment windows when loading data.
      // Legacy drafts stored a single `assessmentWindow` object; normalize to an
      // `assessmentWindows` array.
      if (data && data.schedules) {
        data.schedules = data.schedules.map(schedule => {
          let assessmentWindows = [];
          if (schedule.hasAssessmentWindow === true) {
            if (Array.isArray(schedule.assessmentWindows)) {
              assessmentWindows = schedule.assessmentWindows;
            } else if (schedule.assessmentWindow && typeof schedule.assessmentWindow === 'object') {
              assessmentWindows = [{ name: '', ...schedule.assessmentWindow }];
            }
          }
          return {
            ...schedule,
            hasAssessmentWindow: schedule.hasAssessmentWindow === true,
            assessmentWindows
          };
        });
      }

      return response.data;
    } catch (error) {
      console.error('Error getting study draft data:', error);
      throw error;
    }
  },

  getStudyDrafts: async () => {
    try {
      const response = await api.get('/api/v1/study-builder/study-drafts/');
      console.log('Raw API response:', response);
      console.log('Response data type:', typeof response.data);
      console.log('Response data:', response.data);
      
      // Handle paginated response structure
      const drafts = response.data.results || [];
      console.log('Extracted drafts from results:', drafts);
      return drafts;
    } catch (error) {
      console.error('Error fetching study drafts:', error);
      throw error;
    }
  },

  getSurveys: async (studyId) => {
    try {
      const response = await surveyService.getSurveysByStudyId(studyId);
      return response.surveys || [];
    } catch (error) {
      console.error('Error fetching surveys:', error);
      throw error;
    }
  },

  getSurveysByStudyName: async (studyName, surveyType = 'standard') => {
    const cacheKey = `surveys_${studyName}_${surveyType}`;
    try {
      // Check cache first
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        // Cache valid for 1 second
        if (Date.now() - timestamp < 1 * 1000) {
          console.log(`Using cached ${surveyType} survey data:`, data);
          return data;
        }
      }

      // UNIFIED QUERY: Get both old surveys AND new Assessment Builder assessments
      console.log(`[UNIFIED] Fetching surveys AND assessments for study:`, studyName);
      const response = await api.get(`/api/surveys-and-assessments/study/name/${encodeURIComponent(studyName)}`, {
        params: { type: surveyType }
      });
      
      const surveyData = response.data;
      console.log('[UNIFIED] Received combined data:', surveyData);
      console.log(`[UNIFIED] ${surveyData.meta?.old_surveys || 0} old surveys + ${surveyData.meta?.assessments || 0} assessments = ${surveyData.meta?.total || 0} total`);
      
      // If we have standard surveys, fetch linked surveys data for each
      // NOTE: Only fetch for OLD surveys, not Assessment Builder items (which have source='assessment_builder')
      if (surveyType === 'standard' && surveyData && surveyData.surveys && surveyData.surveys.length > 0) {
        // Get linked surveys for OLD surveys only (not Assessment Builder items)
        const linkPromises = surveyData.surveys.map(async (survey) => {
          try {
            // Only fetch if it's an OLD survey (not Assessment Builder) and has an ID
            // Assessment Builder items have source='assessment_builder' and don't exist in Survey table
            if (survey && survey.id && survey.source !== 'assessment_builder') {
              const linkedSurveys = await surveyService.getLinkedSurveys(survey.id);
              // Attach the linked surveys to the survey object
              survey.linked_surveys = linkedSurveys;
              console.log(`Linked surveys for ${survey.name}:`, linkedSurveys);
            } else {
              // Assessment Builder items don't have linked surveys
              survey.linked_surveys = [];
            }
          } catch (linkError) {
            console.error(`Error fetching linked surveys for survey ${survey.id}:`, linkError);
            // Don't fail the whole operation if one link fetch fails
            survey.linked_surveys = [];
          }
          return survey;
        });
        
        // Wait for all linked survey data to be fetched
        await Promise.all(linkPromises);
      }
      
      // Cache the result
      localStorage.setItem(cacheKey, JSON.stringify({
        data: surveyData,
        timestamp: Date.now()
      }));
      
      return surveyData;
    } catch (error) {
      console.error('Error fetching surveys:', error);
      throw error;
    }
  },

  getReminderTemplates: async (studyDraftId) => {
    const cacheKey = `reminders_${studyDraftId}`;
    try {
      // Check cache first
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        // Cache valid for 1 second
        if (Date.now() - timestamp < 1 * 1000) {
          return data;
        }
      }

      const response = await api.get('/api/v1/study-builder/reminder-templates/', {
        params: { study_draft: studyDraftId }
      });
      const templates = response.data;

      // Cache the result
      localStorage.setItem(cacheKey, JSON.stringify({
        data: templates,
        timestamp: Date.now()
      }));

      return templates;
    } catch (error) {
      console.error('Error fetching reminder templates:', error);
      throw error.response?.data || error.message;
    }
  },

  // Get a study draft by its name
  getStudyDraftByName: async (studyName) => {
    try {
      // First get all drafts
      const response = await api.get('/api/v1/study-builder/study-drafts/', {
        params: { name: studyName }
      });
      
      // Handle paginated response structure
      const drafts = response.data.results || [];
      const draft = drafts.find(d => d.name === studyName);
      
      if (!draft) {
        throw new Error(`No study draft found with name: ${studyName}`);
      }
      
      return draft;
    } catch (error) {
      console.error('Error in getStudyDraftByName:', error);
      throw error.response?.data || error.message;
    }
  },

  /**
   * Project survey occurrences for a draft schedule + hypothetical enrollment date.
   * Backs the Schedule Visualizer (FECOA-1215) — runs against the in-memory schedule
   * config (no save required).
   *
   * @param {Object} scheduleConfig - shape matches Schedule.config
   * @param {string} enrollmentDate - YYYY-MM-DD
   * @param {string} [tz] - IANA tz, defaults to UTC server-side
   * @returns {Promise<{enrollment_date, study_start_date, schedule_type, occurrences: [...]}>}
   */
  previewSchedule: async (scheduleConfig, enrollmentDate, tz) => {
    const response = await api.post('/api/schedules/preview-windows/', {
      schedule_config: scheduleConfig,
      enrollment_date: enrollmentDate,
      ...(tz ? { timezone: tz } : {}),
    });
    return response.data;
  },

  /**
   * FECOA-1238: safely amend the time windows of a LIVE schedule mid-study.
   * Mints a new immutable version, freezes all completed/in-progress/past
   * windows, and regenerates only future windows from the new config.
   *
   * @param {string} scheduleId - the live Schedule UUID
   * @param {Object} config - the full new Schedule.config
   * @param {string} reason - amendment rationale (required)
   * @returns {Promise<{version_number, windows_deleted, windows_created, windows_frozen, ...}>}
   */
  amendSchedule: async (scheduleId, config, reason) => {
    const response = await api.post(`/api/schedules/${scheduleId}/amend/`, {
      config,
      reason,
    });
    return response.data;
  },

  /**
   * FECOA-1238: fetch the version history (original protocol + amendments) for
   * a live schedule, newest first.
   *
   * @param {string} scheduleId - the live Schedule UUID
   * @returns {Promise<Array<{id, version_number, config, effective_from, reason, created_by, created_at}>>}
   */
  getScheduleVersions: async (scheduleId) => {
    const response = await api.get(`/api/schedules/${scheduleId}/versions/`);
    return response.data;
  },
};

export default studyBuilderService;
